import objectHash from "object-hash";
import { useEffect, useMemo, useReducer } from "react";
import { useSelector } from "react-redux";
import type { TabularGeneralSettings } from "../../../../../../../../api/types/svTypes.ts";
import { selectRefreshToken } from "../../../../store/selectors/currentReportSelectors.ts";
import {
  selectReportType,
  selectConditions,
  selectSorts,
  selectTabularFields,
  selectTabularGrouping,
  selectTabularSettingsState,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors";
import { getColumns, getConditionsHash } from "../../../../utilities/gridStateHelper.ts";
import { isTabularConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import type { TabularGridState } from "../../../viewer/tabular/hooks/gridState.types.ts";
import { gridDataStateReducer, initialGridDataState } from "../../../viewer/tabular/storage/gridDataState.ts";
import { getFieldsHash } from "../../common/utilities/getFieldsHash.ts";
import useDataLoadingBuilder from "./useDataLoadingBuilder.ts";

type TabularDataSettings = Omit<
  TabularGeneralSettings,
  | "cashFlow"
  | "detailedView"
  | "showGroupedColumns"
  | "groupSummaryPlace"
  | "subtotalLabelPosition"
  | "subtotalLabelFormatPrefix"
  | "subtotalLabelFormatSuffix"
  | "freezedColumns"
>;

export default function useGridStateBuilder(): TabularGridState {
  const reportType = useSelector(selectReportType);
  const groupAreaValues = useSelector(selectTabularGrouping);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const sorting = useSelector(selectSorts);
  const sortingHash = objectHash(sorting.map((field) => field.config));
  const conditionsAreaValues = useSelector(selectConditions);
  const settingsAreaValues = useSelector(selectTabularSettingsState);
  const dimensions = useSelector(selectDimensions);

  const [state, dispatch] = useReducer(gridDataStateReducer, initialGridDataState);
  const { loadData, cancelDataLoading, failedMeasures, handleInvalidConfiguration } = useDataLoadingBuilder(dispatch);
  const refreshToken = useSelector(selectRefreshToken);

  const columns = useMemo(() => getColumns(fieldsAreaValues, state.columns), [fieldsAreaValues, state.columns]);

  const conditionsHash = useMemo(
    () => getConditionsHash(conditionsAreaValues, fieldsAreaValues),
    [conditionsAreaValues, fieldsAreaValues]
  );
  const fieldsHash = useMemo(
    () =>
      objectHash({
        fields: getFieldsHash(fieldsAreaValues),
        hiddenAggregationFields: fieldsAreaValues
          .filter((field) => field.config.hideAggregation === true)
          .map((field) => field.config.guid)
          .sort(),
      }),
    [fieldsAreaValues]
  );
  const groupingHash = useMemo(
    () => objectHash(groupAreaValues.map(({ id, hideSummaries }) => ({ id, hideSummaries: hideSummaries === true }))),
    [groupAreaValues]
  );
  const settingsHash = useMemo(
    () =>
      objectHash({
        splitFilter: settingsAreaValues.cashFlow?.splitFilter,
        showSplitTotal: settingsAreaValues.cashFlow?.splitFilter?.values.length
          ? settingsAreaValues.cashFlow.showSplitTotal
          : undefined,
        amountFormat: settingsAreaValues.cashFlow?.amountFormat,
        sections: settingsAreaValues.cashFlow?.sections,
        settings: {
          showGrandTotal: settingsAreaValues.showGrandTotal,
          showNonZeroBalanceAccounts: settingsAreaValues.showNonZeroBalanceAccounts,
          hideBlankRows: settingsAreaValues.hideBlankRows,
          hideZeroRows: settingsAreaValues.hideZeroRows,
          hideBlankColumns: settingsAreaValues.hideBlankColumns,
          hideBlankColumnsScope: settingsAreaValues.hideBlankColumnsScope,
          hideBlankColumnIds: settingsAreaValues.hideBlankColumnIds,
          hideZeroColumns: settingsAreaValues.hideZeroColumns,
        } satisfies TabularDataSettings,
      }),
    [settingsAreaValues]
  );

  const configurationValidity = useMemo(() => {
    return isTabularConfigurationValid(conditionsAreaValues, fieldsAreaValues, dimensions, reportType ?? undefined);
  }, [conditionsAreaValues, dimensions, fieldsAreaValues, reportType]);

  useEffect(() => {
    if (configurationValidity.valid) {
      cancelDataLoading();
      loadData(refreshToken);
    } else {
      handleInvalidConfiguration();
      cancelDataLoading();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsHash, fieldsHash, settingsHash, refreshToken, configurationValidity.valid, groupingHash, sortingHash]);

  return {
    rows: state.rows,
    hasResult: state.rows !== initialGridDataState.rows,
    snapshot: state.snapshot,
    columns,
    groupTotals: state.groupTotals,
    grandTotals: state.grandTotals,
    totalCount: state.totalCount,
    loading: state.loading,
    error: state.error,
    configurationValidity,
    failedMeasures,
  };
}
